import React, { useState } from "react";
import PropTypes from "prop-types";
import style from "./InputField.module.scss";
import EyeOpen from "../../../static/images/eye-icon-off.png";
import EyeClose from "../../../static/images/eye-icon.png";

/**
 * InputField — the primary text/number/password input primitive for the app.
 *
 * This is the recommended input for all new forms. Notable design points:
 *   - A single controlled/uncontrolled rule driven by `value !== undefined`, so char-count and
 *
 * `TextField` (`src/components/primary/TextField`) predates this component and is deprecated;
 * it remains only to avoid regressing its existing usages until they're migrated.
 *
 * RHF compatibility:
 *   - RHF v6 style: pass `inputRef={register(rules)}` (ref-callback pattern).
 *   - RHF v7 style: pass `register={register(name)}`, its `{name, onChange, onBlur, ref}`
 *     shape is spread onto the underlying `<input>`.
 *   - `<Controller>` style: use the plain `value` / `onChange` / `onBlur` controlled props.
 */

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

export const InputField = ({
  id,
  name,
  label,
  type = "text",
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyUp,
  onKeyDown,
  disabled = false,
  required = false,
  readOnly = false,
  placeholder = "",
  title = "",
  autoComplete,
  prefix,
  suffix,
  error,
  helperText,
  hint,
  charLimit,
  className = "",
  classNames = {},
  register,
  inputRef,
  showClear = false,
  onClear,
  dataTestId,
  ...rest
}) => {
  const [visible, setVisible] = useState(false);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : undefined;
  const currentDefaultValue = isControlled ? undefined : defaultValue;
  const displayValue = isControlled ? value : defaultValue;

  const errorId = error && id ? `${id}-error` : undefined;
  const helperId = (helperText || hint) && id ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(" ") || undefined;

  const helperMessage = helperText || hint;
  const hasClear = showClear && !!displayValue && !disabled && !readOnly;

  return (
    <div className={cx(style.root, className, classNames.root)}>
      {label && (
        <label htmlFor={id} className={cx(style.label, classNames.label)}>
          {label} {required ? <span className={style.astrick}>&#42;</span> : ""}
        </label>
      )}

      <div className={cx(style.inputWrapper, error && style.inputWrapperError, disabled && style.inputWrapperDisabled, classNames.inputWrapper)}>
        {prefix && <span className={style.prefix}>{prefix}</span>}

        <input
          id={id}
          name={name}
          type={type === "password" && visible ? "text" : type}
          value={currentValue}
          defaultValue={currentDefaultValue}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          placeholder={placeholder}
          title={title}
          autoComplete={autoComplete}
          maxLength={charLimit || undefined}
          ref={inputRef}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          data-testid={dataTestId || id}
          className={cx(style.input, classNames.input)}
          {...register}
          {...rest}
        />

        {suffix && <span className={style.suffix}>{suffix}</span>}

        {type === "password" && (
          <span className={style.eyeToggle} onClick={() => setVisible((v) => !v)} data-testid="eye-toggle">
            <img src={visible ? EyeOpen : EyeClose} alt="" />
          </span>
        )}

        {hasClear && (
          <span className={style.clearIcon} data-testid="clear_icon" onClick={() => onClear && onClear()}>
            <i className="fa fa-times" aria-hidden="true"></i>
          </span>
        )}
      </div>

      {(charLimit || error || helperMessage) && (
        <div className={style.footer}>
          <span>
            {error && (
              <span id={errorId} className={cx(style.errorText, classNames.error)}>
                {error}
              </span>
            )}
            {!error && helperMessage && (
              <span id={helperId} className={cx(style.helperText, classNames.helperText)}>
                {helperMessage}
              </span>
            )}
          </span>
          {charLimit && (
            <span className={cx(style.charCount, classNames.charCount)}>
              {displayValue ? String(displayValue).length : 0}/{charLimit}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

InputField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  type: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  title: PropTypes.string,
  autoComplete: PropTypes.string,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  error: PropTypes.string,
  helperText: PropTypes.node,
  hint: PropTypes.node,
  charLimit: PropTypes.number,
  className: PropTypes.string,
  classNames: PropTypes.shape({
    root: PropTypes.string,
    label: PropTypes.string,
    inputWrapper: PropTypes.string,
    input: PropTypes.string,
    error: PropTypes.string,
    helperText: PropTypes.string,
    charCount: PropTypes.string,
  }),
  register: PropTypes.object,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  showClear: PropTypes.bool,
  onClear: PropTypes.func,
  dataTestId: PropTypes.string,
};
